// src/utils/declaration.js
// ─────────────────────────────────────────────────────────────────────────────
// WHAT AN UNDERTAKING ASKS, AND WHAT THE APPLICANT ACCEPTED.
//
// A declaration is not one promise. Form E's asks three separate things — that
// the foreign repository will be informed, that the applicant is not covered by
// section 3(2), and that the information given is true — and the applicant used
// to accept all three with a single tick underneath. That is one signature over
// three undertakings: the record could not say which of them was read, and the
// filed document could not show it either.
//
// So each statement is acknowledged on its own, in front of the statement, and
// the acknowledgement is part of the record. This module owns:
//
//   • what the statements ARE          — one definition, so the form the
//     applicant ticks and the preview they approve can never disagree about
//     how many undertakings there were or what they said
//   • the key each acknowledgement is stored under
//   • how an application saved BEFORE this existed is read back
//
// It owns no markup. The three renderers each draw it their own way.
//
// BACKWARD COMPATIBILITY, and why it is not optional: every application already
// submitted carries a single `declarationAgreed: true` and no per-statement
// keys. Read literally, all of those would reopen as "nothing accepted" and
// their summaries would show three unticked undertakings — the record would
// misrepresent what the applicant actually did. A lone legacy `true` therefore
// means every statement was accepted, and `declarationAgreed` is still written
// on save (true only when all of them are), so the backend sees what it always
// saw.
// ─────────────────────────────────────────────────────────────────────────────

import { toUndertaking } from './undertaking';

const toArray = v => {
  if (Array.isArray(v)) return v.filter(Boolean).map(s => String(s).trim()).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
};

const DEFAULT_AGREEMENT =
  'I/We agree to the above undertaking and confirm that the information provided is true and correct.';

/**
 * The declaration copy, normalised for display.
 *
 * Lived in two files — the form renderer and the preview — with the two copies
 * already drifting on the fallback text. One definition now.
 */
export function normalizeDeclarationContent(sec) {
  const declarationText = toArray(sec?.declarationText).map(toUndertaking);
  const agreementLines = toArray(sec?.agreementText).map(toUndertaking);
  if (declarationText.length === 0 && agreementLines.length > 0) {
    return { declarationText: agreementLines, agreementText: DEFAULT_AGREEMENT };
  }
  return { declarationText, agreementText: agreementLines.join(' ') || DEFAULT_AGREEMENT };
}

/** The sub-declarations of a nested undertaking (Form 13), or []. */
export const declSubGroups = sec =>
  (sec?.fields || []).filter(f => f?.sectionType === 'subSection' && Array.isArray(f.fields));

/* Whether this declaration needs accepting at all. Download-and-upload
   declarations (Form 13) opt out via requireAgreement:false. */
export const declRequiresAgreement = sec => sec?.requireAgreement !== false;

/* The key one statement's acknowledgement is stored under. Same shape as the
   fill-in-the-blank keys (__declblank_…) so both round-trip through save and
   restore the same way, and neither can collide with a template field name. */
export const declAckKey = (sec, subIdx, paraIdx) =>
  `__declack_${sec?.sectionId || 'decl'}_${subIdx}_${paraIdx}`;

/* A fill-in-the-blank inside a declaration paragraph, and the key its answer is
   stored under. These lived in the form renderer, which meant the preview
   could not tell a blank from a tick — and that distinction is exactly
   what decides how a statement is accepted. */
export const DECL_BLANK_RE = /_{3,}/g;
export const countBlanks = text => (String(text ?? '').match(/_{3,}/g) || []).length;
export const declBlankKey = (sec, subIdx, paraIdx, blankIdx) =>
  `__declblank_${sec?.sectionId || 'decl'}_${subIdx}_${paraIdx}_${blankIdx}`;

/* ── HOW A STATEMENT IS ACCEPTED ──────────────────────────────────────────
   Not every undertaking is accepted the same way.

     lead   "I/We declare that:" introduces the points below it. It promises
            nothing on its own, so there is nothing to accept — asking the
            applicant to tick a colon is asking them to agree to punctuation.

     blank  "I/We ______ undertake that I shall inform the foreign repository…"
            asks the applicant to WRITE something into the sentence. Filling it
            in IS the acceptance; a checkbox beside it would ask for the same
            consent twice, in two ways, on one line.

     check  Everything else — a plain statement, accepted by ticking it.

   Derived from the sentence, never configured per form, so a template that adds
   a fourth undertaking gets the right control for it with no code change. */
export const STATEMENT_MODES = { LEAD: 'lead', BLANK: 'blank', CHECK: 'check' };

/* A LEAD-IN, not an undertaking: a short opener that introduces the points
   below it ("I/We declare that:"). It is not a promise on its own, so it is not
   something to tick — putting a checkbox against it would ask the applicant to
   accept a colon. Recognised structurally: the first line, short, with real
   statements following it, and ending the way an opener ends.

   Templates spell that ending several ways, and every one must be recognised —
   Form 8's "I / We declare that:-" ended in ":-", missed a colon-only test, and
   was shown as undertaking no. 1 with a checkbox the applicant had to tick:
     "…that:"   "…that:-"   "…that: -"   "…that:—"   "…that"   "…as follows"
   A real undertaking is a complete sentence ("…is false."), so it never ends
   like this. */
const LEAD_MAX_CHARS = 120;
const LEAD_END_RE = /(?:[:：]|\b(?:that|follows|following))\s*[:：]?\s*[-–—]*\s*$/i;
const isLeadIn = (text, index, total) => {
  const s = String(text || '').trim();
  return index === 0 && total > 1 && s.length <= LEAD_MAX_CHARS && LEAD_END_RE.test(s);
};

/**
 * The statements of ONE declaration block, in render order.
 *
 * `paraIdx` is the POSITION IN THE ARRAY THAT WAS PASSED IN, kept even when a
 * blank entry between statements is skipped. It has to be: the fill-in-the-blank
 * keys (__declblank_…) are built from the same position by collectDeclBlanks,
 * which walks the array unfiltered. Renumbering after a filter would key a
 * blank by an index nothing else agrees on, and the applicant's typed answer
 * would come back attached to the wrong sentence.
 *
 * @param {object} sec     the declaration section
 * @param {Array}  texts   the block's own lines (a sub-declaration's, or the section's)
 * @param {number} subIdx  which sub-declaration (0 for a flat one)
 * @returns {Array<{key:string, paraIdx:number, text:string, lead:boolean, ordinal:number}>}
 */
export function statementsOf(sec, texts, subIdx = 0) {
  const raw = Array.isArray(texts)
    ? texts
    : (typeof texts === 'string' && texts.trim() ? [texts] : []);
  const kept = raw
    .map((t, paraIdx) => ({ text: toUndertaking(String(t ?? '')), paraIdx }))
    .filter(x => x.text.trim());
  let ordinal = 0;
  return kept.map((x, i) => {
    const lead = isLeadIn(x.text, i, kept.length);
    const blanks = countBlanks(x.text);
    if (!lead) ordinal += 1;
    const mode = lead ? STATEMENT_MODES.LEAD
      : blanks > 0 ? STATEMENT_MODES.BLANK
      : STATEMENT_MODES.CHECK;
    return {
      key: declAckKey(sec, subIdx, x.paraIdx),
      paraIdx: x.paraIdx, subIdx, text: x.text, blanks, mode,
      lead, ordinal: lead ? 0 : ordinal,
    };
  });
}

/**
 * The RAW paragraph array one block renders from.
 *
 * THE array every paragraph index is counted against — the statements, the
 * fill-in-the-blank keys and the signatory blank all have to agree on it. Three
 * separate helpers used to derive it three different ways: the blank collector
 * read `declarationText` unfiltered, the signatory-blank finder read it with the
 * agreementText fallback and blanks dropped, and the renderer read the
 * normalised list. On a declaration with an empty entry between statements, or
 * one whose copy lives in agreementText, they disagreed about which paragraph
 * index a blank belonged to — and the applicant's typed answer would come back
 * attached to the wrong sentence.
 *
 * @param {object} sec
 * @param {number} subIdx  which sub-declaration (0, or ignored, for a flat one)
 */
export function blockParagraphs(sec, subIdx = 0) {
  const groups = declSubGroups(sec);
  if (groups.length > 0) {
    const g = groups[subIdx];
    return Array.isArray(g?.declarationText) ? g.declarationText : [];
  }
  const asList = v => Array.isArray(v) ? v : (typeof v === 'string' && v.trim() ? [v] : []);
  const decl = asList(sec?.declarationText);
  // A declaration whose copy lives in agreementText renders THOSE lines.
  return decl.some(t => String(t ?? '').trim()) ? decl : asList(sec?.agreementText);
}

/* ── The signatory-name blank ──────────────────────────────────────────────
   Most forms give the undertaking a separate "Name" input. Some (Form E)
   instead write the name INTO the undertaking sentence as a blank:

       "I/We ________ undertake that I shall inform the foreign repository …"

   That blank means exactly the same thing as the Name field, so it is filled
   the same way — from the applicant name(s) on Page 1. It is identified by what
   comes immediately BEFORE it (the paragraph's "I/We" opener), never by
   position, so a blank holding a date, a place or a patent number is left alone.

   Only FLAT declarations qualify. A nested one (Form 13: Applicant / Supervisor
   / Recipient) opens each block with "I/We" while meaning a DIFFERENT person
   each time, and filling the applicant's name into all three would be wrong.

   This lived in DynamicFormRenderer, the screen that FILLS a form. The screen
   that READS one back — the submitted-application view, built by
   services/formService — could not see it, so it had no way to
   tell that blank apart from any other and printed whatever stale text happened
   to be stored against it. */
const SIGNATORY_BLANK_LEAD_RE = /(?:^|[^a-z])(?:i\s*\/\s*we|we\s*\/\s*i|i\s*(?:and|&)\s*we|i|we)\s*[,:;-]?\s*$/i;
export const isSignatoryBlankLead = textBeforeBlank => SIGNATORY_BLANK_LEAD_RE.test(String(textBeforeBlank ?? ''));

/**
 * The key of a flat declaration's signatory-name blank, or '' when it has none.
 * The FIRST such blank wins: a paragraph re-stating "I/We ___" further down is
 * the same signatory, and one filled-in name is what the form asks for.
 */
export function declSignatoryBlankKey(sec) {
  if (!sec || declSubGroups(sec).length > 0) return '';
  const texts = blockParagraphs(sec, 0);
  for (let paraIdx = 0; paraIdx < texts.length; paraIdx++) {
    // parts[i] is the text immediately before blank i (the last part trails the
    // final blank and is not followed by one).
    const parts = String(texts[paraIdx] ?? '').split(DECL_BLANK_RE);
    for (let b = 0; b < parts.length - 1; b++) {
      if (isSignatoryBlankLead(parts[b])) return declBlankKey(sec, 0, paraIdx, b);
    }
  }
  return '';
}

/**
 * Every statement of a declaration, flat or nested, each tagged with the block
 * it belongs to. This is the list the form ticks and the preview lists — one
 * answer to "how many undertakings were there?".
 */
export function declarationStatements(sec) {
  const groups = declSubGroups(sec);
  if (groups.length > 0) {
    return groups.flatMap((g, si) =>
      statementsOf(sec, blockParagraphs(sec, si), si)
        .map(s => ({ ...s, subIdx: si, blockTitle: g.sectionName ? toUndertaking(g.sectionName) : '' })));
  }
  return statementsOf(sec, blockParagraphs(sec, 0), 0)
    .map(s => ({ ...s, subIdx: 0, blockTitle: '' }));
}

/** Every statement that must be accepted somehow — by filling or by ticking. */
export const acknowledgeableStatements = sec =>
  declarationStatements(sec).filter(s => s.mode !== STATEMENT_MODES.LEAD);

/** Only the ones accepted with a tick — the statements that own an ack key. */
export const checkboxStatements = sec =>
  declarationStatements(sec).filter(s => s.mode === STATEMENT_MODES.CHECK);

/** The keys holding the answers written into one statement's blanks. */
export const statementBlankKeys = (sec, st) =>
  Array.from({ length: st?.blanks || 0 }, (_, b) => declBlankKey(sec, st.subIdx || 0, st.paraIdx, b));

/**
 * Is this particular statement accepted, by whichever means it asks for?
 * A blank-bearing statement is accepted when every blank in it has been filled.
 */
export function statementAccepted(sec, st, values = {}, legacyAgreed = false) {
  if (!st || st.mode === STATEMENT_MODES.LEAD) return true;
  if (st.mode === STATEMENT_MODES.BLANK) {
    return statementBlankKeys(sec, st).every(k => String(values?.[k] ?? '').trim() !== '');
  }
  return isStatementAcknowledged(values, st.key, legacyAgreed);
}

/**
 * Has this statement been accepted?
 *
 * `legacyAgreed` is the pre-existing single `declarationAgreed` flag. It stands
 * in for every statement ONLY while no per-statement answer has been recorded
 * anywhere in this declaration — the moment one has, the explicit answers are
 * the record and a stale blanket flag must not overrule them.
 */
export const isStatementAcknowledged = (values, key, legacyAgreed = false) => {
  const v = values?.[key];
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return !!legacyAgreed;
};

/** Does this declaration carry any explicit per-statement answer yet? */
export const hasExplicitAcks = (sec, values) =>
  checkboxStatements(sec).some(s => values?.[s.key] !== undefined);

/**
 * How far through the undertakings the applicant is.
 *
 * @returns {{total:number, accepted:number, allAccepted:boolean, pending:Array}}
 */
export function acknowledgementState(sec, values = {}) {
  const statements = acknowledgeableStatements(sec);
  // A legacy blanket "agreed" stands in only while nothing explicit is recorded.
  const legacy = !hasExplicitAcks(sec, values) && !!values?.declarationAgreed;
  const accepted = statements.filter(s => statementAccepted(sec, s, values, legacy));
  return {
    total: statements.length,
    accepted: accepted.length,
    allAccepted: statements.length > 0 && accepted.length === statements.length,
    pending: statements.filter(s => !statementAccepted(sec, s, values, legacy)),
  };
}

/**
 * Is the declaration as a whole accepted? A declaration that asks nothing to be
 * ticked (requireAgreement:false, or no statements at all) is not blocking.
 */
export function declarationAccepted(sec, values = {}) {
  if (!declRequiresAgreement(sec)) return true;
  const { total, allAccepted } = acknowledgementState(sec, values);
  if (total === 0) return !!values?.declarationAgreed;
  return allAccepted;
}

/**
 * The per-statement answers to write for an application whose consent was
 * recorded as one blanket flag — so a legacy record is MATERIALISED once, on
 * open, instead of being re-guessed on every read.
 *
 * @returns {object} keys to merge into form state ({} when nothing to do)
 */
export function materialiseLegacyAcks(sec, values = {}) {
  if (!declRequiresAgreement(sec)) return {};
  if (!values?.declarationAgreed) return {};
  if (hasExplicitAcks(sec, values)) return {};
  const out = {};
  checkboxStatements(sec).forEach(s => { out[s.key] = true; });
  return out;
}

/**
 * The undertakings of a declaration, resolved against the applicant's answers.
 *
 * This is what the form builder hands to the preview. The preview MUST NOT
 * re-derive it: by the time it sees the section its blanks have been
 * substituted with the applicant's words, so "does this sentence contain a
 * blank?" no longer has the answer it had on the form. Resolving once, where the
 * template and the values are both in hand, is what keeps the form and the
 * preview agreeing about which undertaking was accepted and how.
 *
 * @returns {Array<{key,paraIdx,subIdx,ordinal,text,mode,accepted}>}
 */
export function resolveUndertakings(sec, values = {}) {
  const legacy = !hasExplicitAcks(sec, values) && !!values?.declarationAgreed;
  return declarationStatements(sec).map(st => ({
    key: st.key,
    paraIdx: st.paraIdx,
    subIdx: st.subIdx,
    ordinal: st.ordinal,
    text: st.text,
    mode: st.mode,
    accepted: statementAccepted(sec, st, values, legacy),
  }));
}

export default declarationStatements;
